/** @file Inserts interior points into a counter-clockwise triangulation by splitting triangles or edges. */
import { flatOrient } from './flatOrient';
import { pointsScale } from './pointsScale';

interface InsertCtx {
  pts: ArrayLike<number>;
  tris: number[];
  /** Distance below which a point counts as lying on a line (1e-7 of the point set's size). */
  tol: number;
}

interface Hit {
  /** Triangle index (into triples). */
  t: number;
  /** −1 strictly inside; k = on the edge opposite corner k. */
  edge: number;
}

/** Signed distance of point v from the line a→b (> 0 on the left). */
function side(pts: ArrayLike<number>, a: number, b: number, v: number): number {
  const len = Math.hypot((pts[2 * b] ?? 0) - (pts[2 * a] ?? 0), (pts[2 * b + 1] ?? 0) - (pts[2 * a + 1] ?? 0));
  return len > 0 ? flatOrient(pts, a, b, v) / len : 0;
}

/** The triangle holding v; within tol of one edge = on that edge; within tol of two (a corner) = null. */
function locate(ctx: InsertCtx, v: number): Hit | null {
  const { pts, tris, tol } = ctx;
  for (let t = 0; t < tris.length / 3; t++) {
    const [a = 0, b = 0, c = 0] = tris.slice(3 * t, 3 * t + 3);
    const o = [side(pts, b, c, v), side(pts, c, a, v), side(pts, a, b, v)];
    if (o.some((x) => x < -tol)) continue;
    const onEdges = o.filter((x) => x <= tol).length;
    if (onEdges === 0) return { t, edge: -1 };
    return onEdges === 1 ? { t, edge: o.findIndex((x) => x <= tol) } : null;
  }
  return null;
}

/** The triangle holding the directed edge from→to and its third corner; null when none (a hull edge). */
function findDirected(tris: number[], from: number, to: number): { t: number; s: number } | null {
  for (let t = 0; t < tris.length / 3; t++) {
    for (let k = 0; k < 3; k++) {
      if (tris[3 * t + k] === from && tris[3 * t + ((k + 1) % 3)] === to) return { t, s: tris[3 * t + ((k + 2) % 3)] ?? 0 };
    }
  }
  return null;
}

function splitInside(ctx: InsertCtx, t: number, v: number): boolean {
  const [a = 0, b = 0, c = 0] = ctx.tris.slice(3 * t, 3 * t + 3);
  ctx.tris.splice(3 * t, 3, a, b, v);
  ctx.tris.push(b, c, v, c, a, v);
  return true;
}

/** Splits both triangles beside edge q→r (opposite corner `hit.edge`); false on a hull edge or when a part would be a sliver. */
function splitEdge(ctx: InsertCtx, hit: Hit, v: number): boolean {
  const corner = (k: number): number => ctx.tris[3 * hit.t + ((hit.edge + k) % 3)] ?? 0;
  const [a, q, r] = [corner(0), corner(1), corner(2)];
  const other = findDirected(ctx.tris, r, q);
  if (!other || side(ctx.pts, other.s, r, v) <= ctx.tol || side(ctx.pts, q, other.s, v) <= ctx.tol) return false;
  ctx.tris.splice(3 * hit.t, 3, a, q, v);
  ctx.tris.splice(3 * other.t, 3, r, v, other.s);
  ctx.tris.push(a, v, r, v, q, other.s);
  return true;
}

/** Inserts points hullCount.. of `p` into `triangles` (mutated); points outside, on the hull or near a corner are skipped. */
export function insertPoints(p: ArrayLike<number>, hullCount: number, triangles: number[]): { triangles: number[]; skipped: number[] } {
  const ctx: InsertCtx = { pts: p, tris: triangles, tol: 1e-7 * pointsScale(p) };
  const skipped: number[] = [];
  for (let v = hullCount; v < p.length / 2; v++) {
    const hit = locate(ctx, v);
    let done = false;
    if (hit) done = hit.edge < 0 ? splitInside(ctx, hit.t, v) : splitEdge(ctx, hit, v);
    if (!done) skipped.push(v);
  }
  return { triangles, skipped };
}
